import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { THEMES } from '@americanexpress/consumer-travel-base';
import { useFeatureFlag } from '@americanexpress/one-experiments';
import { useSelector } from 'react-redux';
import { getActiveLocaleSelector, getTenancySelector } from '@americanexpress/travel-selectors';
import { RenderModule } from 'holocron';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { hasUnusedCreditRule } from '../../constants/featureRulesConstants';
import style from './Hero.scss';
import { useIsCenturionContext } from '../../context/isCenturionContext';
import CenturionWordmark from '../CenturionWordmark/CenturionWordmark';
import getLineOfBusinessFromPath from '../../utils/getLineOfBusinessUtils';
import AbandonHotel from '../AbandonHotel/AbandonHotel';
import {
  MODULE_NAME, UNUSED_TICKETS_MODULE_NAME, ADVISORY_MODULE_NAME, SEARCH_MODULE_NAME,
} from '../../constants/modules';
import { useDataFromOneCms } from '../../fetchye/useOneCmsFetchye';
import { DISABLE_PACKAGES_FF, E_TICKET_BANNER_FF } from '../../constants/oneXpConstants';

const Hero = ({
  setSelectedTab,
  isLoggedIn,
  pathname,
}) => {
  const isCenturion = useIsCenturionContext();
  const tenancy = useSelector(getTenancySelector);
  const locale = useSelector(getActiveLocaleSelector);
  const { properties: packagesFFProperties } = useFeatureFlag(
    { featureFlagName: DISABLE_PACKAGES_FF });
  const isPackagesDisabledByFF = packagesFFProperties?.[tenancy]?.includes(locale);
  const { properties: eTicketBannerFFProperties } = useFeatureFlag(
    { featureFlagName: E_TICKET_BANNER_FF });
  const isETicketBannerDisplayedByFF = eTicketBannerFFProperties?.[tenancy]?.includes(locale);
  const hasUnusedCredit = useConditionalFeature(hasUnusedCreditRule);

  const centurionHeroImageUrl = useDataFromOneCms('centurionHeroImageUrl');
  const heroImageUrl = useDataFromOneCms('heroImageUrl');

  return (
    <div data-testid={`${MODULE_NAME}-home-hero`} className="position-relative pad-2-t-md-up pad-2-b-md-up">
      <div className="container margin-b-md-down">
        <RenderModule moduleName={ADVISORY_MODULE_NAME} />
      </div>
      <div
        id="heroBackgroundImage"
        style={{ backgroundImage: `url(${isCenturion ? centurionHeroImageUrl : heroImageUrl})` }}
        className={clsx('card-img-bg hidden-sm-down position-absolute', style.heroImage, {
          [style.centurionHeroImage]: isCenturion,
        })}
      />
      {isCenturion && <CenturionWordmark />}
      <AbandonHotel />
      <div
        className="container margin-t-md-up margin-b-md-up pad-0-sm-down position-relative"
      >
        <RenderModule
          moduleName={SEARCH_MODULE_NAME}
          props={{
            theme: THEMES.THEME_WHITE,
            activeTab: getLineOfBusinessFromPath(isPackagesDisabledByFF, pathname),
            onActiveTabChange: setSelectedTab,
          }}
        />
        {
          isLoggedIn && isETicketBannerDisplayedByFF && hasUnusedCredit ? (
            <div className="margin-t">
              <RenderModule moduleName={UNUSED_TICKETS_MODULE_NAME} />
            </div>
          ) : null
        }
      </div>
    </div>
  );
};

Hero.propTypes = {
  setSelectedTab: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  pathname: PropTypes.string.isRequired,
};

export default Hero;
